<template>
  <div>
    <TextInput
      label="First Name"
      placeholder="Elon"
      :model-value="application.firstName"
      required
      :error="getErrorMessage('firstName', 'First name is required')"
      @update:model-value="updateField('firstName', $event)"
    />
    <TextInput
      class="mt-4"
      label="Last Name"
      placeholder="Husk"
      :model-value="application.lastName"
      required
      :error="getErrorMessage('lastName', 'Last name is required')"
      @update:model-value="updateField('lastName', $event)"
    />
    <PhoneInput
      class="mt-4"
      label="Phone Number"
      placeholder="+1 (281) 330 8004"
      :model-value="application.phone"
      required
      :error="getErrorMessage('phone', 'Phone number is required')"
      @update:model-value="updateField('phone', $event)"
    />
    <TextInput
      class="mt-4"
      label="Email"
      placeholder="you@example.com"
      :model-value="application.email"
      required
      :error="getErrorMessage('email', 'Email is required')"
      @update:model-value="updateField('email', $event)"
    />
    <FileInput
      label="Resume"
      :optional="!record.resumeRequired"
      limits="any Document"
      :model-value="application.resume"
      accept=".pdf, .doc, .docx"
      :error="getErrorMessage('resume', 'Resume is required')"
      @update:model-value="updateField('resume', $event)"
    />
    <CheckboxInput
      v-if="record.qualifyingFilter"
      class="mt-4"
      :description="`I am NOT currently a licensed ${humanizedJobType} in ${record.state}, but would like assistance becoming one`"
      :model-value="application.notLicensedAgent"
      :error="getErrorMessage('licenseStatus', `You must select one of the license status options`)"
      @update:model-value="updateLicenseStatus('notLicensedAgent', $event)"
    />
    <CheckboxInput
      v-if="application.notLicensedAgent"
      class="mt-4 ml-6"
      :description="`I have started the required coursework`"
      :model-value="application.courseworkStarted"
      :error="getErrorMessage('coursework', 'You must select one of the coursework options')"
      @update:model-value="updateCourseworkStatus('courseworkStarted', $event)"
    />
    <CheckboxInput
      v-if="application.notLicensedAgent"
      class="mt-4 ml-6"
      name="started_coursework"
      :description="`I have not started the required coursework and would like assistance in doing so`"
      :model-value="application.courseworkNotStarted"
      :error="getErrorMessage('coursework', 'You must select one of the coursework options')"
      @update:model-value="updateCourseworkStatus('courseworkNotStarted', $event)"
    />
    <CheckboxInput
      v-if="record.qualifyingFilter"
      name="licensed_agent"
      class="mt-4"
      :description="`I AM currently a licensed ${humanizedJobType} in ${record.state}`"
      :model-value="application.licensedAgent"
      :error="getErrorMessage('licenseStatus', `You must select one of the license status options`)"
      @update:model-value="updateLicenseStatus('licensedAgent', $event)"
    />
    <TextInput
      v-if="record.qualifyingFilter && application.licensedAgent"
      class="mt-4 ml-6"
      label="RE License Number"
      :optional="true"
      placeholder="123456"
      :model-value="application.reLicenseNumber"
      @update:model-value="updateField('reLicenseNumber', $event)"
    />
    <CheckboxInput
      class="mt-4"
      name="accept_terms"
      description="I consent to receive text messages at the phone number provided regarding this application. Msg & data rates may apply. Msg frequency varies. Reply HELP for help. Reply STOP to unsubscribe."
      :model-value="application.contactMethodsOptIn"
      @update:model-value="updateField('contactMethodsOptIn', $event)"
    />
    <p class="mt-4 text-xs text-center leading-5 text-gray-500">
      All form submissions governed by Humaniz, Inc.'s
      <a href="https://www.humaniz.io/terms" target="_blank">Terms of Service</a> and
      <a href="https://www.humaniz.io/privacy" target="_blank">Privacy Policy</a>.
    </p>
  </div>
</template>

<script setup>
import { reactive, computed, defineProps, defineEmits } from 'vue';

const props = defineProps({
  record: Object,
});

const emit = defineEmits(['update:modelValue']);

const application = reactive({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  reLicenseNumber: '',
  resume: '',
  notLicensedAgent: false,
  courseworkStarted: false,
  courseworkNotStarted: false,
  licensedAgent: false,
  contactMethodsOptIn: false,
});

const errors = reactive({
  firstName: false,
  lastName: false,
  phone: false,
  email: false,
  licenseStatus: false,
  coursework: false,
  resume: false,
});

const humanizedJobType = computed(() => {
  return props.record.jobType.replace(/_/g, ' ');
});

const updateField = (field, value) => {
  application[field] = value;
  update();
};

const updateLicenseStatus = (field, value) => {
  if (field === 'notLicensedAgent' && value) {
    application.notLicensedAgent = true;
    application.licensedAgent = false;
  } else if (field === 'licensedAgent' && value) {
    application.licensedAgent = true;
    application.notLicensedAgent = false;
    application.courseworkStarted = false;
    application.courseworkNotStarted = false;
  }

  application[field] = value;
  update();
};

const updateCourseworkStatus = (field, value) => {
  if (field === 'courseworkStarted' && value) {
    application.courseworkStarted = true;
    application.courseworkNotStarted = false;
  } else if (field === 'courseworkNotStarted' && value) {
    application.courseworkNotStarted = true;
    application.courseworkStarted = false;
  }

  application[field] = value;
  update();
};

const getErrorMessage = (field, message) => {
  return errors[field] ? message : '';
};

const validateFields = () => {
  errors.firstName = !application.firstName?.trim();
  errors.lastName = !application.lastName?.trim();
  errors.phone = !application.phone?.trim();
  errors.email = !application.email?.trim();
  errors.resume = props.record.resumeRequired && !application.resume;

  if (props.record.qualifyingFilter) {
    if (application.notLicensedAgent) {
      errors.coursework = !application.courseworkStarted && !application.courseworkNotStarted;
      errors.licenseStatus = false;
    } else {
      errors.licenseStatus = !application.licensedAgent;
      errors.coursework = false;
    }
  } else {
    errors.licenseStatus = false;
    errors.coursework = false;
  }
};

const isFormValid = () => {
  validateFields();

  const basicFieldsValid = !Object.entries(errors)
    .filter(([key]) => ['firstName', 'lastName', 'phone', 'email', 'resume'].includes(key))
    .some(([_, hasError]) => hasError);

  if (!basicFieldsValid) return false;

  if (props.record.qualifyingFilter) {
    if (application.notLicensedAgent) {
      if (!application.courseworkStarted && !application.courseworkNotStarted) {
        errors.coursework = true;
        return false;
      }
    } else if (!application.licensedAgent) {
      errors.licenseStatus = true;
      return false;
    }
  }

  return true;
};

const update = () => {
  validateFields();
  emit('update:modelValue', { application, isValid: isFormValid() });
};

const validate = () => {
  return isFormValid();
};

const validateForm = () => {
  return validate();
};

defineExpose({
  validate,
  validateForm,
});
</script>
